import React, { useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";

// Define grouped menus with icons and routes
export const horizontalMenuGroups = {
  management: [
    { path: "/roles", label: "Roles Management", icon: "ti ti-id" },
    { path: "/permissions", label: "Task Permissions", icon: "ti ti-lock" },
    { path: "/branch", label: "Branch Management", icon: "ti ti-building-bank" },
    { path: "/branch/users", label: "User Management", icon: "ti ti-users" },
  ],
  setup: [
    { path: "/currencies", label: "Currencies", icon: "ti ti-currency-dollar" },
    { path: "/accounting", label: "Charts of Account", icon: "ti ti-chart-dots" },
    { path: "/locations", label: "Locations", icon: "ti ti-map-pin" },
    { path: "/warehouse", label: "Warehouse", icon: "ti ti-building-warehouse" },
    { path: "/metal-rates", label: "Metal Rate", icon: "ti ti-coin" },
    { path: "/suppliers", label: "Suppliers", icon: "ti ti-truck-delivery" },
    { path: "/customers", label: "Customers", icon: "ti ti-user-dollar" },
    { path: "/setup/taxes", label: "Taxes", icon: "ti ti-percentage" },
    { path: "/setup/units", label: "Units", icon: "ti ti-ruler-measure" },
    { path: "/brands", label: "Brands", icon: "ti ti-tag" },
    { path: "/product-attributes", label: "Attributes", icon: "ti ti-list-details" },
  ],
  import: [
    { path: "/import", label: "Import Products", icon: "ti ti-upload" },
    { path: "/import/ideal-stock", label: "Ideal Stock Import", icon: "ti ti-database-import" },
    { path: "/import/stock-remove", label: "Stock Removal (Sale)", icon: "ti ti-trending-down" },
    { path: "/import/purchases", label: "Purchase Import", icon: "ti ti-file-import" },
    { path: "/import/images", label: "Import Product Images", icon: "ti ti-photo" },
  ],
  final_products: [
    { path: "/categories", label: "Categories", icon: "ti ti-category-2" },
    { path: "/raw-products", label: "Purchased Products / Item Master", icon: "ti ti-box" },
    { path: "/products", label: "Final Products", icon: "ti ti-package" },
  ],
  purchase_system: [
    { path: "/payment/supplier", label: "Vendor Payments", icon: "ti ti-credit-card" },
    { path: "/pushed-purchase", label: "Purchase Request", icon: "ti ti-file-plus" },
    { path: "/approved-purchase", label: "Approved Purchases", icon: "ti ti-badge-check" },
    { path: "/raw-product/stock", label: "Allocate Items", icon: "ti ti-transfer" },
    { path: "/raw-product/purchases", label: "Purchase Entry", icon: "ti ti-file-invoice" },
    { path: "/raw-products-stock", label: "Product Stock", icon: "ti ti-archive" },
  ],
  sales_system: [
    { path: "/reports/sales", label: "Sales Report", icon: "ti ti-chart-bar" },
    { path: "/reports/stock", label: "Stock Report", icon: "ti ti-chart-line" },
    { path: "/tenders/sale", label: "Sales Request", icon: "ti ti-file-search" },
    { path: "/tenders/cost", label: "Cost Estimation", icon: "ti ti-calculator" },
    { path: "/tenders/order", label: "Orders", icon: "ti ti-shopping-cart" },
    { path: "/tenders/delivered", label: "Delivery", icon: "ti ti-truck" },
    { path: "/tenders/production", label: "Production Orders", icon: "ti ti-building-factory" },
  ],
  accounts: [
    { path: "/accounts/sales-bills", label: "Sales Bills", icon: "ti ti-receipt" },
    { path: "/accounts/due-bills", label: "Due Bills", icon: "ti ti-receipt" },
    { path: "/accounts/claimed-bills", label: "Claimed Bills", icon: "ti ti-receipt" },
    { path: "/accounts/bills", label: "Bill Management", icon: "ti ti-file-invoice" },
    { path: "/accounts/credit-notes", label: "Credit Notes", icon: "ti ti-notes" },
    { path: "/sales-credit-notes", label: "Sales Credit Notes", icon: "ti ti-credit-card-refund" },
    { path: "/accounts/assets", label: "Assets Management", icon: "ti ti-briefcase" },
    { path: "/accounts/expenses", label: "Expenses Management", icon: "ti ti-wallet" },
  ],
  tasks: [
    { path: "/task/assigned-to-you", label: "For You", icon: "ti ti-user-check" },
    { path: "/task/assigned-by-you", label: "By You", icon: "ti ti-user-share" },
  ],
};

const HorizontalMenu = () => {
  const location = useLocation();
  const path = location.pathname;
  const scrollContainerRef = useRef(null);
  const activeItemRef = useRef(null);

  // Determine which group to show using exact match or path prefix
  const activeGroup = Object.entries(horizontalMenuGroups).find(([_, items]) =>
    items.some((item) => path === item.path || path.startsWith(item.path + "/"))
  );

  const activeMenus = activeGroup ? activeGroup[1] : [];

  // Scroll active menu item into view on mount or route change
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [path]);

  // Support horizontal mouse wheel scrolling across devices
  const handleWheel = (e) => {
    if (scrollContainerRef.current && e.deltaY !== 0) {
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  if (!activeGroup || activeMenus.length === 0) {
    return null;
  }

  return (
    <div
      className="horizontal-menu-wrapper"
      id="horizontal-menu"
      role="navigation"
      aria-label="Sub Navigation"
    >
      <div id="sidebar-menu-3" className="sidebar-menu">
        <div className="main-menu horizontal-scroll-wrapper">
          <ul
            className="nav nav-pills horizontal-scroll-menu"
            ref={scrollContainerRef}
            onWheel={handleWheel}
          >
            {activeMenus.map((menu) => {
              const isActive =
                path === menu.path ||
                (menu.path !== "/" && path.startsWith(menu.path + "/"));
              return (
                <li
                  key={menu.path}
                  className="submenu"
                  ref={isActive ? activeItemRef : null}
                >
                  <Link
                    to={menu.path}
                    className={`btn ${
                      isActive ? "btn-primary active" : "btn-outline-primary"
                    }`}
                  >
                    <i className={`${menu.icon || "ti ti-layout-grid"} fs-16 me-2`} />
                    <span>{menu.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HorizontalMenu;
